import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useSchool } from '../../../contexts/SchoolContext';
import {
  SETTINGS_SECTIONS, SETTINGS_ROOT, settingsPath, canOpen, isListed,
  type SettingsSection, type SettingsContext,
} from './sections';

function Spinner() {
  return (
    <div className="flex h-40 items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
    </div>
  );
}

/**
 * The registry's view of the signed-in user — or null while the school is
 * still loading. activeModules is empty for one render before the school
 * document arrives (see SchoolContext), so deciding before then would bounce
 * someone who is perfectly entitled. Same wait ModuleRoute makes.
 */
export function useSettingsContext(): SettingsContext | null {
  const { appUser } = useAuth();
  const { hasModule, sendTier, schoolLoading } = useSchool();
  if (!appUser || schoolLoading) return null;
  return { role: appUser.role, hasModule, sendTier };
}

/** Wraps one section's element: role and module from the registry. */
export function SectionGuard({ section, children }: {
  readonly section: SettingsSection;
  readonly children: React.ReactNode;
}) {
  const ctx = useSettingsContext();
  if (!ctx) return <Spinner />;
  if (!canOpen(section, ctx)) return <Navigate to={SETTINGS_ROOT} replace />;
  return <>{children}</>;
}

/**
 * /school/settings itself: go to the first section this person would see in
 * the rail, else Home. Listed, not merely openable — a staff member who is
 * not the SENCo MAY open the SEND section by its address (it self-gates, as
 * /send/setup always did), but "Settings" should not deliver them to a page
 * they can only read. The Home fallback is also what stops a loop: a guard
 * bounces someone to the index, and the index must not bounce them back.
 */
export function SettingsIndex() {
  const ctx = useSettingsContext();
  const { hash, search } = useLocation();
  if (!ctx) return <Spinner />;
  const first = SETTINGS_SECTIONS.find((s) => isListed(s, ctx));
  if (!first) return <Navigate to="/home" replace />;
  return <Navigate to={{ pathname: settingsPath(first.id), search, hash }} replace />;
}
